import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import apiClient from "../api/CustomerClient";
// import { subscribeToOrderUpdates } from "./utils/socket";
import { subscribeToOrderUpdates, getSocketStatus } from "./utils/socket";

const CustomerOrderStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const restaurant_id =
    location.state?.restaurant_id ||
    new URLSearchParams(location.search).get("restaurant_id") ||
    localStorage.getItem("restaurant_id");

  const orderId =
    location.state?.orderId ||
    new URLSearchParams(location.search).get("orderId") ||
    localStorage.getItem("orderId");

  const token =
    location.state?.token ||
    new URLSearchParams(location.search).get("token") ||
    localStorage.getItem("CustomerToken");

  const [orderDetails, setOrderDetails] = useState({});
  const [statuses, setStatuses] = useState([
    { label: "Order Received", value: "Pending", checked: false, icon: "📥" },
    { label: "Order Confirmed", value: "Approved", checked: false, icon: "✅" },
    { label: "Being Prepared", value: "Approved", checked: false, icon: "👨‍🍳" },
    { label: "Ready", value: "Ready", checked: false, icon: "🎉" },
    { label: "Complete", value: "Completed", checked: false, icon: "📦" },
  ]);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [detailedOrderInfo, setDetailedOrderInfo] = useState(null);
  const [restaurantDetail, setRestaurantDetail] = useState("");
  const [orderCancelled, setOrderCancelled] = useState(false);
  const [expandedItems, setExpandedItems] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const restaurantTokenRef = useRef(token);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/restaurant/get-restaurant/${restaurant_id}`,
        {
          headers: {
            Authorization: `Bearer ${restaurantTokenRef.current}`,
          },
        }
      );
      if (response.data && response.data.data) {
        setRestaurantDetail(response.data.data);
        if (response.data.data.token !== restaurantTokenRef.current) {
          restaurantTokenRef.current = response.data.data.token;
        }
      }
    } catch (error) {
      console.log("Error fetching restaurant details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrderStatus = async () => {
    try {
      const response = await apiClient.get(
        `/customer/order-summary/${restaurant_id}/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${restaurantTokenRef.current}`,
          },
        }
      );
      if (response.status === 200) {
        setOrderDetails(response.data);
        const fetchedStatus = response.data.status;
        if (fetchedStatus === "Cancelled") {
          setOrderCancelled(true);
        } else {
          const statusIndex = statuses.findIndex(
            (status) => status.value === fetchedStatus
          );
          const updatedStatuses = statuses.map((status, index) => ({
            ...status,
            checked: index <= statusIndex,
          }));
          setStatuses(updatedStatuses);
        }
      }
    } catch (error) {
      console.error("Error fetching order status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await apiClient.get(
        `/customer/order-summary/${restaurant_id}/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${restaurantTokenRef.current}`,
          },
        }
      );
      setDetailedOrderInfo(response.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  const handleShowOrderDetails = () => {
    if (!detailedOrderInfo) {
      fetchOrderDetails();
    }
    setShowOrderDetails(!showOrderDetails);
  };

  const toggleExpandedItems = () => {
    setExpandedItems(!expandedItems);
  };

  const handleBackToMenu = () => {
    if (restaurantTokenRef.current) {
      navigate(`/menu-display?token=${restaurantTokenRef.current}`, {
        replace: true,
      });
    } else {
      console.error("Token is missing. Unable to navigate to menu display.");
    }
  };

  useEffect(() => {
    fetchOrderStatus();
    if (restaurant_id) {
      fetchRestaurantDetails();
    }
  }, [restaurant_id]);

  useEffect(() => {
    if (!restaurant_id || !orderId) return;

    const unsubscribe = subscribeToOrderUpdates(restaurant_id, (orderData) => {
      try {
        const parsedData =
          typeof orderData === "string" ? JSON.parse(orderData) : orderData;

        // order_id arrives as a number over the socket (JSON) but orderId can be
        // a string (URL query param / emailed link) or a number (in-app
        // navigation with the already-parsed order object) - normalize both
        // sides so a legitimate update for this order is never silently dropped.
        if (String(parsedData.order_id) === String(orderId)) {
          setStatuses((prevStatuses) => {
            const statusIndex = prevStatuses.findIndex(
              (status) => status.value === parsedData.status
            );
            return prevStatuses.map((status, index) => ({
              ...status,
              checked: index <= statusIndex,
            }));
          });

          setOrderDetails((prev) => {
            if (prev.status !== parsedData.status) {
              return { ...prev, status: parsedData.status };
            }
            return prev;
          });

          if (parsedData.status === "Cancelled") {
            setOrderCancelled(true);
          }
        }
      } catch (error) {
        console.error("Error processing socket update:", error, orderData);
      }
    }, restaurantTokenRef.current, orderId);

    return () => {
      unsubscribe();
    };
  }, [restaurant_id, orderId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading order status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={handleBackToMenu}
            className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 font-semibold bg-white rounded-lg shadow-sm hover:shadow-md transition-all mb-6"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Menu
          </button>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {restaurantDetail?.restaurant_name}
            </h1>
            <p className="text-gray-600 text-lg mb-4">
              {restaurantDetail?.address}, {restaurantDetail?.city},{" "}
              {restaurantDetail?.state}
            </p>
            <p className="text-gray-700 font-medium">
              📞 {restaurantDetail?.phone?.replace(
                /(\d{3})(\d{3})(\d{4})/,
                "$1-$2-$3"
              )}
            </p>
          </div>
        </div>

        {/* Order Status Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Order Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold mb-2">Order Status</h2>
                <p className="text-blue-100">Tracking your order in real-time</p>
              </div>
              <button
                onClick={fetchOrderStatus}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          <div className="p-8">
            {orderCancelled ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-red-600 mb-2">
                  Order Cancelled
                </h3>
                <p className="text-gray-600">We're sorry, your order has been cancelled.</p>
              </div>
            ) : (
              <>
                {/* Progress Steps */}
                <div className="space-y-6 mb-8">
                  {statuses.map((status, index) => (
                    <div
                      key={index}
                      className={`flex items-center p-4 rounded-xl border-2 transition-all ${
                        status.checked
                          ? "border-green-500 bg-green-50 transform scale-105 shadow-lg"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                        status.checked
                          ? "bg-green-500 text-white shadow-lg"
                          : "bg-gray-300 text-gray-600"
                      }`}>
                        {status.checked ? "✓" : status.icon}
                      </div>
                      <div className="ml-4 flex-1">
                        <span
                          className={`text-lg font-semibold ${
                            status.checked
                              ? "text-green-700"
                              : "text-gray-700"
                          }`}
                        >
                          {status.label}
                        </span>
                        {status.checked && (
                          <p className="text-green-600 text-sm mt-1">
                            Completed successfully
                          </p>
                        )}
                      </div>
                      {status.checked && (
                        <div className="text-green-500">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-2xl font-bold text-gray-900">
                      Order Total
                    </span>
                    <span className="text-3xl font-bold text-blue-600">
                      ${orderDetails.total_amount?.toFixed(2) || "0.00"}
                    </span>
                  </div>

                  <button
                    onClick={handleShowOrderDetails}
                    className="w-full bg-gray-800 text-white py-4 px-6 rounded-xl hover:bg-gray-900 transition-colors font-semibold text-lg flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Order #{detailedOrderInfo?.order_id || orderDetails?.order_id}
                    {showOrderDetails ? " (Hide Details)" : " (Show Details)"}
                  </button>

                  {showOrderDetails && detailedOrderInfo && (
                    <div className="mt-6 bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">
                        Order Details
                      </h3>
                      <div className="space-y-3">
                        {detailedOrderInfo.items
                          .slice(
                            0,
                            expandedItems ? detailedOrderInfo.items.length : 3
                          )
                          .map((item, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 truncate">
                                  {item.product_name}
                                </p>
                              </div>
                              <div className="flex items-center space-x-6 ml-4">
                                <span className="text-gray-600 whitespace-nowrap">
                                  Qty: {item.quantity}
                                </span>
                                <span className="font-bold text-gray-900 whitespace-nowrap">
                                  ${item.price.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        {detailedOrderInfo.items.length > 3 && (
                          <div
                            onClick={toggleExpandedItems}
                            className="text-center py-3 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <span className="text-blue-600 font-medium">
                              {expandedItems
                                ? "Show less"
                                : `+${detailedOrderInfo.items.length - 3} more items`}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="border-t mt-6 pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-700">
                            Sub-total:
                          </span>
                          <span className="text-xl font-bold text-gray-900">
                            ${detailedOrderInfo.total_amount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Real-time Status Indicator */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Real-time updates active
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderStatus;